import { generateId, guardString } from "../strings";
import type { Tables } from "../types";
import type {
	DocumentStructure,
	DocumentStructureFileItem,
	DocumentStructureFolderItem,
} from "../types/documents";
import { getDocumentStructureFolderSize } from "./get-document-structure-folder-size";

export type DocumentStructureSortBy = "name" | "size";
export type DocumentStructureSortDirection = "asc" | "desc";

/**
 * This function creates a document structure from an array of items.
 * It filters out items that do not have a file name containing a slash ("/").
 * It returns a structure with items categorized as files.
 * @param items
 * @returns
 */
export function createDocumentStructure<T extends { file: Tables<"files"> }>(
	files: T[],
	sortBy: "name" | "size" = "name",
	sortDirection: "asc" | "desc" = "asc",
): DocumentStructure<T> {
	const rootItems: (DocumentStructureFileItem<T> | DocumentStructureFolderItem<T>)[] = [];
	function insert(
		parts: string[],
		fileItem: DocumentStructureFileItem<T>,
		items: (DocumentStructureFileItem<T> | DocumentStructureFolderItem<T>)[],
		prevHead = "",
	) {
		const [head, ...rest] = parts;

		if (rest.length === 0) {
			items.push(fileItem);
			return;
		}

		// Look for existing folder
		let folder = items.find(
			(i): i is DocumentStructureFolderItem<T> => i.type === "folder" && i.name === head,
		) as DocumentStructureFolderItem<T>;

		const fullPath = [prevHead, head].filter(Boolean).join("/");

		if (!folder) {
			folder = {
				id: head || guardString(generateId()),
				fullPath,
				type: "folder",
				name: head || guardString(null),
				items: [],
			};
			items.push(folder);
		}

		insert(rest, fileItem, folder.items, fullPath);
	}

	for (const item of files) {
		const pathParts = item.file.fileName.split("/");

		const fileNode: DocumentStructureFileItem<T> = {
			id: item.file.id,
			type: "file",
			item,
			file: item.file,
		};

		insert(pathParts, fileNode, rootItems);
	}

	function sortItems(
		items: (DocumentStructureFileItem<T> | DocumentStructureFolderItem<T>)[],
		sortBy: "name" | "size",
		sortDirection: "asc" | "desc",
	) {
		items.sort((a, b) => {
			if (a.type !== b.type) {
				return a.type === "folder" ? -1 : 1;
			}
			if (sortBy === "size") {
				const aSize = a.type === "folder" ? getDocumentStructureFolderSize(a) : a.file.size;
				const bSize = b.type === "folder" ? getDocumentStructureFolderSize(b) : b.file.size;
				if (sortDirection === "asc") {
					return aSize - bSize;
				} else {
					return bSize - aSize;
				}
			} else if (sortBy === "name") {
				const aName = a.type === "folder" ? a.name : a.file.fileName.split("/").pop()!;
				const bName = b.type === "folder" ? b.name : b.file.fileName.split("/").pop()!;
				if (sortDirection === "asc") {
					return aName.localeCompare(bName);
				}
				return bName.localeCompare(aName);
			} else {
				return 0;
			}
		});

		for (const item of items) {
			if (item.type === "folder") {
				sortItems(item.items, sortBy, sortDirection);
			}
		}
	}

	sortItems(rootItems, sortBy, sortDirection);

	return {
		items: rootItems,
	};
}
